import { useMutation, useQueryClient, type QueryClient, type UseMutationOptions } from '@tanstack/react-query';
// Concrete modules, not the @/lib and @/utils barrels: createEntityCrudHooks
// builds hooks from this at module-load time, and the barrels form a cycle
// back into @/hooks that would leave it undefined at that point.
import { supabase } from '@/lib/supabase';
import { handleUnexpectedError } from '@/lib/supabase-error-handler';
import { getCurrentAdminDetails, type CurrentAdminDetails } from '@/lib/admin-details';
import { logActivity, type ActivityLogChanges, type ActivityLogMetadata } from '@/utils/activity-logger';

/** One activity_logs row, written after the mutation's write succeeds. */
export interface ActivityEntry {
  action: string;
  entityType: string;
  entityId: string;
  changes?: ActivityLogChanges;
  metadata?: ActivityLogMetadata;
  memberId?: string | null;
}

export interface LoggedMutationContext {
  /** The signed-in admin, fetched once on first call (for created_by_* columns). */
  admin: () => Promise<CurrentAdminDetails>;
  /** Queue an activity entry. Written only if the mutation function resolves. */
  log: (entry: ActivityEntry) => void;
}

export interface LoggedMutationOptions<TData, TVariables>
  extends Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn'> {
  /** For error telemetry, e.g. 'useCreateClassLocation'. */
  hookName: string;
  /** For the user-facing error, e.g. 'creating class location'. */
  operation: string;
  mutationFn: (variables: TVariables, ctx: LoggedMutationContext) => Promise<TData>;
  /** What to refresh on success — usually a family helper from @/lib/query-invalidation. */
  invalidate?: (queryClient: QueryClient, data: TData, variables: TVariables) => unknown;
}

/**
 * useMutation for the common admin write: look up who is acting, write, record
 * it in the activity log, refresh the screens that show it.
 *
 * - Activity entries are written after the write succeeds and BEFORE
 *   onSuccess, so an invalidation that refreshes a change log finds the new
 *   row already there. logActivity never throws (it reports and returns), so a
 *   logging failure cannot fail a write that already happened.
 * - Unexpected errors go through handleUnexpectedError with hookName and
 *   operation, as the hand-written hooks did in their own try/catch.
 */
export function useLoggedMutation<TData, TVariables = void>({
  hookName,
  operation,
  mutationFn,
  invalidate,
  onSuccess,
  ...options
}: LoggedMutationOptions<TData, TVariables>) {
  const queryClient = useQueryClient();

  return useMutation<TData, Error, TVariables>({
    ...options,
    mutationFn: async (variables) => {
      const entries: ActivityEntry[] = [];
      let adminPromise: Promise<CurrentAdminDetails> | null = null;
      const ctx: LoggedMutationContext = {
        admin: () => (adminPromise ??= getCurrentAdminDetails(supabase)),
        log: (entry) => {
          entries.push(entry);
        },
      };

      try {
        const data = await mutationFn(variables, ctx);
        for (const e of entries) {
          await logActivity(
            e.action,
            e.entityType,
            e.entityId,
            e.changes,
            e.metadata,
            e.memberId ?? undefined,
          );
        }
        return data;
      } catch (error) {
        handleUnexpectedError(error, { hookName, operation });
      }
    },
    onSuccess: (...args) => {
      const [data, variables] = args;
      invalidate?.(queryClient, data, variables);
      return onSuccess?.(...args);
    },
  });
}
